import {
    collection,
    getVariantByParentType,
    globalCounterPath,
    parentTypeFor,
    readGlobalCount,
} from "../_variants/index.js";
import {
    makeAttached,
    makeDropdown,
    next,
    pad
} from "../../../utils.js";

const revisionQuality = {
    "Approver": {
        "By": "N/A",
        "Completed": false,
        "Date": "N/A"
    },
    "Checker": {
        "By": "N/A",
        "Completed": false,
        "Date": "N/A"
    },
    "Compiler": {
        "By": "N/A",
        "Completed": false,
        "Date": "N/A"
    }
};

export const addDOI = ({ form, pageItem, store, attachedItems, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");
    const doiCollectionTail = doiCollection.split("/")[1];
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = docCollection.split("/")[1];
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = drawingCollection.split("/")[1];
    const effectivityCollection = "Database/Effectivities";
    const effectivityCollectionTail = "Effectivities";
    const doiParentType = parentTypeFor(variant, "DOIs");
    
    const counterPath = globalCounterPath(variant, "counts/database/doi/count");

    form.setConfig({
        "title": "Add DOI",
        "type": "push",
        "location": doiCollection,
        "fields": [{
            "label": "Number",
            "type": "value",
            value() {
                return `${variant.docPrefix}DOI-${pad(readGlobalCount(store, variant, ["database", "doi"]))}`
            }
        }, {
            "label": "Comment",
            "name": "Comment",
            "type": "text",
            "divClass": "col-span-2"
        }, {
            "label": "Select Documents",
            "name": docCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[docCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Drawings",
            "name": drawingCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[drawingCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Effectivities",
            "name": effectivityCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[effectivityCollectionTail].items, "Number", "ID")
        }],
        async preSubmitHook({ data, db }) {
            const number = await db.utils.allocateCount(counterPath);
            return {
                "Date": {
                    "Created": new Date().toISOString(),
                    "Cancelled": "N/A",
                    "Signedoff": "N/A"
                },
                "Cancelled": false,
                "Aircraft": pageItem.Aircraft,
                "Signedoff": false,
                "Author": store.getters.userName,
                "Parent": pageItem.Number,
                "ParentID": pageItem.ID,
                "ParentType": meta.ParentType,
                "Number": `${variant.docPrefix}DOI-${pad(number)}`,
                [docCollectionTail]: makeAttached(data[docCollectionTail]),
                [drawingCollectionTail]: makeAttached(data[drawingCollectionTail]),
                [effectivityCollectionTail]: makeAttached(data[effectivityCollectionTail]),
                "Revision": 1,
                "RevisionID": "--R1",
                "Revisions": {
                    "--R1": {
                        "Revision": 1,
                        "Reason": "Document Created",
                        "ID": "--R1",
                        "Date": new Date().toISOString(),
                        "Quality": revisionQuality
                    }
                },
            };
        },
        postSubmitHook({ db, data, typeRef }) {
            data.ID = typeRef.key;
            db.set(`Database/${meta.ParentType}/${pageItem.ID}/${doiCollectionTail}/${data.ID}`, true);
            db.utils.addAllTo(data, docCollection, docCollectionTail, doiParentType);
            db.utils.addAllTo(data, drawingCollection, drawingCollectionTail, doiParentType);
            db.utils.addAllTo(data, effectivityCollection, effectivityCollectionTail, doiParentType);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const editDOI = async ({ form, item, attachedItems, store, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");
    const doiCollectionTail = doiCollection.split("/")[1];
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = docCollection.split("/")[1];
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = drawingCollection.split("/")[1];
    const effectivityCollection = "Database/Effectivities";
    const effectivityCollectionTail = "Effectivities";
    const doiParentType = parentTypeFor(variant, "DOIs");

    form.setValues({
        "Comment": item.Comment,
        [docCollectionTail]: Object.keys(item[docCollectionTail] || {}),
        [drawingCollectionTail]: Object.keys(item[drawingCollectionTail] || {}),
        [effectivityCollectionTail]: Object.keys(item[effectivityCollectionTail] || {})
    });
    form.setConfig({
        "title": "Update DOI",
        "type": "update",
        "location": `${doiCollection}/${item.ID}`,
        "set": true,
        "fields": [{
            "label": "Comment",
            "name": "Comment",
            "type": "text",
            "divClass": "col-span-3"
        }, {
            "label": "Select Documents",
            "name": docCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[docCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Drawings",
            "name": drawingCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[drawingCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Effectivities",
            "name": effectivityCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[effectivityCollectionTail].items, "Number", "ID")
        }],
        preSubmitHook({ data }) {
            return {
                [docCollectionTail]: makeAttached(data[docCollectionTail]),
                [drawingCollectionTail]: makeAttached(data[drawingCollectionTail]),
                [effectivityCollectionTail]: makeAttached(data[effectivityCollectionTail])
            };
        },
        postSubmitHook({ db, data, typeRef }) {
            db.utils.addAllTo(data, docCollection, docCollectionTail, doiParentType);
            db.utils.addAllTo(data, drawingCollection, drawingCollectionTail, doiParentType);
            db.utils.addAllTo(data, effectivityCollection, effectivityCollectionTail, doiParentType);
        }
    });
    await form.getDropdownData();
    
    form.toggle();
};

export const cancelDOI = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");

    form.setConfig({
        "title": "Cancel DOI",
        "description": `Are you sure you want to cancel DOI ${item.Number}?`,
        "type": "update",
        "location": `${doiCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook(){
            return {
                "Cancelled": true,
                "Date/Cancelled": new Date().toISOString(),
                revisionQuality
            };
        },
    });
    form.toggle();
};

export const signoffDOI = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");

    form.setConfig({
        "title": "Signoff DOI",
        "description": `Are you sure you want to signoff DOI ${item.Number}?`,
        "type": "update",
        "location": `${doiCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Signedoff": true,
                "Date/Signedoff": new Date().toISOString()
            };
        },
    });
    form.toggle();
};

export const linkDOI = ({ form, pageItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");
    const doiCollectionTail = parentTypeFor(variant, "DOIs");

    form.setConfig({
        "title": "Link DOI",
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}`,
        "fields": [{
            "label": "DOI",
            "name": "DOI",
            "type": "dropdown",
            "labelKey": "Number",
            "idKey": "ID",
            "dataLocation": doiCollection
        }],
        preSubmitHook({liveInputData}) {
            return {
                "DOI": null,
                [`${doiCollectionTail}/${liveInputData.DOI}`]: true
            }
        },
        postSubmitHook({ db, liveInputData }) {
            db.set(`${doiCollection}/${liveInputData.DOI}/${meta.ParentType}/${pageItem.ID}`, true);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const reviseDOI = ({form, item, store, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");

    const nextRevision = next(item.Revision);
    const nextRevisionID = `--R${nextRevision}`;

    form.setConfig({
        "title": "Revise DOI",
        "type": "update",
        "location": `${doiCollection}/${item.ID}`,
        "description": `Revise ${item.Number} from ${item.Revision} to ${nextRevision}`,
        "fields": [{
            "label": "Reason",
            "name": `Revisions[${nextRevisionID}][Reason]`,
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/DocumentRevisionTypes",
            "divClass": "cols-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                "Revision": nextRevision,
                "RevisionID": nextRevisionID,
                "Revisions": {
                    [nextRevisionID]: {
                        "ID": nextRevisionID,
                        "Date": (new Date()).toISOString(),
                        "Revision": nextRevision,
                        "RevisedBy": store.state.user.Basic.name,
                        "Quality": revisionQuality,
                        "Reason": data.Revisions[nextRevisionID].Reason
                    }
                }
            };
        }
    });

    form.getDropdownData();
    form.toggle();
};

export const checkDOI = type => ({ form, item, store, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const doiCollection = collection(variant, "DOIs");

    form.setConfig({
        "title": `${type} Check DOI`,
        "type": "update",
        "descriptionStyles": "bg-orange rounded font-bold m-2",
        "location": `${doiCollection}/${item.ID}`,
        "submitButtonLabel": "I confirm",
        "fields": [],
        preSubmitHook() {
            return {
                [type]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/By`]: store.getters.userName,
                [`Revisions/${item.RevisionID}/Quality/${type}/Completed`]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/Date`]: (new Date()).toISOString()
            };
        },
    });
    form.toggle();
};
